import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, DollarSign, TrendingUp, Receipt, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPFinance = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, invoicesRes, expensesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/finance/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/finance/invoices`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/finance/expenses`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Finance & Billing</h1>
          <p className="text-gray-600 mt-1">Manage invoices, payments, and expenses</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90"><Plus className="w-4 h-4 mr-2" />New Invoice</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Revenue</p><p className="text-2xl font-bold text-green-600">₹{((stats?.total_revenue || 0) / 10000000).toFixed(2)}Cr</p></div><DollarSign className="w-10 h-10 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Expenses</p><p className="text-2xl font-bold text-red-600">₹{((stats?.total_expenses || 0) / 10000000).toFixed(2)}Cr</p></div><TrendingUp className="w-10 h-10 text-red-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Paid Invoices</p><p className="text-2xl font-bold text-gray-900">{stats?.paid_invoices || 0}</p></div><Receipt className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Pending Invoices</p><p className="text-2xl font-bold text-orange-600">{stats?.pending_invoices || 0}</p></div><Receipt className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Recent Invoices</h2><div className="space-y-3">{invoices.slice(0, 5).map(inv => (<div key={inv.id} className="flex justify-between items-center p-3 bg-gray-50 rounded"><div><p className="font-medium">{inv.invoice_number}</p><p className="text-sm text-gray-600">{inv.client_name}</p></div><div className="text-right"><p className="font-bold">₹{(inv.total_amount / 100000).toFixed(2)}L</p><span className={`text-xs px-2 py-1 rounded ${inv.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{inv.payment_status}</span></div></div>))}</div></div></Card>
        <Card><div className="p-6"><h2 className="text-xl font-bold mb-4">Recent Expenses</h2><div className="space-y-3">{expenses.slice(0, 5).map(exp => (<div key={exp.id} className="flex justify-between items-center p-3 bg-gray-50 rounded"><div><p className="font-medium">{exp.description}</p><p className="text-sm text-gray-600">{exp.category}</p></div><p className="font-bold text-red-600">₹{(exp.amount / 100000).toFixed(2)}L</p></div>))}</div></div></Card>
      </div>
    </div>
  );
};

export default ERPFinance;